/**
 * Not Authorized Page - Phase 11
 * 
 * Shown when user lacks permission to access a route
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, ArrowLeft, Home, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePermissions } from '@/context/PermissionsContext';

export function NotAuthorizedPage({ requiredPermissions = [] }) {
  const navigate = useNavigate();
  const { adminProfile, getPermissionInfo, permissions } = usePermissions();
  
  return (
    <div className="flex-1 flex items-center justify-center p-6" data-testid="not-authorized-page">
      <Card className="max-w-md w-full bg-card border-destructive/20">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <ShieldAlert className="w-8 h-8 text-destructive" />
          </div>
          <CardTitle className="text-xl font-heading">Access Denied</CardTitle>
          <CardDescription>
            You don't have permission to access this page
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* User info */}
          {adminProfile && (
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <p className="text-muted-foreground">Logged in as:</p>
              <p className="font-medium">{adminProfile.username}</p>
              <p className="text-xs text-muted-foreground capitalize">Role: {adminProfile.role}</p>
            </div>
          )}
          
          {/* Required permissions */}
          {requiredPermissions.length > 0 && (
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-sm text-yellow-500 font-medium mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Required Permissions:
              </p>
              <ul className="space-y-1">
                {requiredPermissions.map(perm => {
                  const info = getPermissionInfo(perm);
                  const hasPerm = permissions.includes(perm);
                  return (
                    <li 
                      key={perm} 
                      className={`text-sm flex items-center gap-2 ${hasPerm ? 'text-emerald-400' : 'text-red-400'}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${hasPerm ? 'bg-emerald-400' : 'bg-red-400'}`} />
                      {info.name}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => navigate(-1)}
              data-testid="go-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            <Button 
              className="flex-1"
              onClick={() => navigate('/superadmin')}
              data-testid="go-dashboard-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
          
          <p className="text-xs text-center text-muted-foreground pt-2">
            Contact your administrator if you believe you should have access.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default NotAuthorizedPage;
